import { useEffect, useMemo } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  InputNumber,
  Select,
  Space,
  Switch,
  Table,
  Typography,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import {
  BASIS_OPTIONS,
  configFromForm,
  dayStartOptions,
  formFromConfig,
  formProblems,
  tablesFor,
  to12h,
  type OnlineEarningConfig,
  type OnlineEarningForm,
  type RateCol,
  type StageRow,
  type StageTableSpec,
} from './onlineEarningForm';

export const ONLINE_EARNING_QUERY_KEY = ['admin', 'call-pricing', 'online-earning'];
const QUERY_KEY = ONLINE_EARNING_QUERY_KEY;

/** The stored stage config — shared by the card and the pricing table above it. */
export function useOnlineEarningConfig() {
  return useQuery({
    queryKey: ONLINE_EARNING_QUERY_KEY,
    queryFn: async () =>
      (await api.get<OnlineEarningConfig>('/admin/call-pricing/online-earning')).data,
  });
}

/** Zones offered in the picker; the stored zone is always included. */
function timezoneOptions(current?: string): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
  const all = intl.supportedValuesOf?.('timeZone') ?? ['Asia/Kolkata', 'UTC'];
  return current && !all.includes(current) ? [current, ...all] : all;
}

const NEW_STAGE: StageRow = { toHours: null, dv: 0, da: 0, rv: 0, ra: 0 };

const RATE_TITLE: Record<RateCol, string> = {
  dv: 'Direct video ₹/min',
  da: 'Direct audio ₹/min',
  rv: 'Random video ₹/min',
  ra: 'Random audio ₹/min',
};

/**
 * Listener daily earning stages (PEER listeners only).
 *
 * The listener's time today — online time, or connected call time, per the
 * Calculation basis — picks their earning rate for the NEXT call they start; a
 * running call keeps the rate it started with. Caller coin prices are not part
 * of this and never change with it.
 */
export function OnlineEarningCard() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm<OnlineEarningForm>();
  const values = Form.useWatch([], form);

  const cfg = useOnlineEarningConfig();

  useEffect(() => {
    if (cfg.data) form.setFieldsValue(formFromConfig(cfg.data));
  }, [cfg.data, form]);

  const save = useMutation({
    mutationFn: async (v: OnlineEarningForm) =>
      (await api.put<OnlineEarningConfig>('/admin/call-pricing/online-earning', configFromForm(v)))
        .data,
    onSuccess: () => {
      message.success('Daily earning stages saved — applies to calls started from now on.');
      void qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (e: unknown) => {
      const data = (e as { response?: { data?: { message?: string; fields?: Record<string, string> } } })
        .response?.data;
      message.error(data?.fields?.config ?? data?.message ?? 'Save failed');
    },
  });

  const problems = useMemo(() => (values?.stages ? formProblems(values) : []), [values]);
  // Only the tables the chosen basis runs are shown; the others keep their
  // rows in the form (and are sent on save) so switching back loses nothing.
  const tables = tablesFor(values?.basis);

  const rupee = (name: (string | number)[]) => (
    <Form.Item name={name} rules={[{ required: true }]} style={{ margin: 0 }}>
      <InputNumber min={0} max={10000} step={0.5} precision={2} prefix="₹" style={{ width: 110 }} />
    </Form.Item>
  );

  const stageTable = (t: StageTableSpec) => {
    const rows: StageRow[] = values?.[t.list] ?? [];
    const startOf = (i: number) => (i === 0 ? 0 : rows[i - 1]?.toHours ?? null);
    const label = t.list === 'stages' ? 'Stage' : t.title.replace(' stages', '') + ' stage';
    return (
      <div key={t.list} data-testid={`stage-table-${t.list}`} style={{ marginBottom: 8 }}>
        {tables.length > 1 && (
          <Typography.Title level={5} style={{ marginTop: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t.title}
          </Typography.Title>
        )}
        <Form.List name={t.list}>
          {(fields, { add, remove }) => (
            <>
              <Table
                rowKey="key"
                size="small"
                pagination={false}
                dataSource={fields}
                scroll={{ x: 900 }}
                style={{ marginBottom: 12 }}
                columns={[
                  {
                    title: 'Step reached',
                    key: 'n',
                    width: 120,
                    render: (_: unknown, f: { name: number }) => `${label} ${f.name + 1}`,
                  },
                  {
                    title: `From (${t.hours})`,
                    key: 'from',
                    width: 150,
                    render: (_: unknown, f: { name: number }) => {
                      const s = startOf(f.name);
                      return s == null ? '—' : `${s} h`;
                    },
                  },
                  {
                    title: `To (${t.hours})`,
                    key: 'to',
                    width: 150,
                    render: (_: unknown, f: { name: number }) =>
                      f.name === fields.length - 1 ? (
                        <Typography.Text type="secondary">Unlimited</Typography.Text>
                      ) : (
                        <Form.Item name={[f.name, 'toHours']} rules={[{ required: true }]} style={{ margin: 0 }}>
                          <InputNumber min={0.01} max={24} step={0.5} precision={2} suffix="h" style={{ width: 110 }} />
                        </Form.Item>
                      ),
                  },
                  ...t.cols.map((c) => ({
                    title: RATE_TITLE[c],
                    key: c,
                    render: (_: unknown, f: { name: number }) => rupee([f.name, c]),
                  })),
                  {
                    title: '',
                    key: 'x',
                    width: 90,
                    render: (_: unknown, f: { name: number }) => (
                      <Can permission="call_pricing.edit">
                        <Button size="small" danger onClick={() => remove(f.name)}>
                          Remove
                        </Button>
                      </Can>
                    ),
                  },
                ]}
              />
              <Can permission="call_pricing.edit">
                <Button onClick={() => add({ ...NEW_STAGE })} style={{ marginBottom: 16 }}>
                  + Add stage
                </Button>
              </Can>
            </>
          )}
        </Form.List>
      </div>
    );
  };

  return (
    <Card title="Listener daily earning stages" loading={cfg.isLoading}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Peer listeners earn more per minute as their time today grows. The Calculation basis picks WHICH time counts and which stage table applies: Online (recorded online sessions → the Stages table), Audio/Video Combined (connected audio + video call time → one Combined table), or Audio/Video Separate (connected audio time → the Audio table and connected video time → the Video table, each progressing on its own). When you first switch to a call basis its table starts as a copy of your Online stages; edit it freely — every table is kept when you switch again. Time resets at the start of each earning day. The rate is picked when a call STARTS and stays fixed for that call. Caller coin prices are not affected. When switched off, the listener earnings in the table above apply."
      />
      <Form<OnlineEarningForm> form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
        <Space size="large" wrap>
          <Form.Item name="enabled" label="Enabled" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="basis" label="Calculation basis" rules={[{ required: true }]}>
            <Select
              data-testid="earning-basis"
              style={{ width: 220 }}
              options={BASIS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            />
          </Form.Item>
          <Form.Item name="timezone" label="Timezone" rules={[{ required: true }]}>
            <Select
              showSearch
              style={{ width: 240 }}
              options={timezoneOptions(values?.timezone).map((z) => ({ value: z, label: z }))}
            />
          </Form.Item>
          <Form.Item
            name="dayStart"
            label="Earning day starts at (and ends at the same time next day)"
            rules={[{ required: true }]}
          >
            <Select
              style={{ width: 120 }}
              options={dayStartOptions(values?.dayStart).map((t) => ({ value: t, label: to12h(t) }))}
            />
          </Form.Item>
        </Space>

        {tables.map(stageTable)}
        {/* The tables the basis does not run stay registered (hidden) so their rows survive a switch. */}
        {(['stages', 'audioStages', 'videoStages', 'combinedStages'] as const)
          .filter((l) => !tables.some((t) => t.list === l))
          .map((l) => (
            <Form.List key={l} name={l}>
              {() => null}
            </Form.List>
          ))}

        {problems.length > 0 && (
          <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={problems.join(' ')} />
        )}
        <Can permission="call_pricing.edit">
          <Button type="primary" htmlType="submit" loading={save.isPending} disabled={problems.length > 0}>
            Save daily earning stages
          </Button>
        </Can>
      </Form>
    </Card>
  );
}
